import './table-hac-shipping-admin.scss';
import React, {useEffect, useState, useCallback} from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { saveAs } from 'file-saver';
import Grid from '@mui/material/Grid2';
import {useTranslation} from 'react-i18next';
import apiClient from '../../../../utils/apiClient';
import {Alert, Snackbar} from '@mui/material';
import {AdapterDayjs} from "@mui/x-date-pickers/AdapterDayjs";
import {Controller, useForm} from "react-hook-form";
import {DateField} from "@mui/x-date-pickers/DateField";
import dayjs from "dayjs";
import {LocalizationProvider} from "@mui/x-date-pickers/LocalizationProvider";
import SortableHeaderCell from '../../common/table/sortable-header-cell';
import TableResultsSummary from '../../common/table/table-results-summary';
import handleEmptyTableResponse from '../../common/table/utils/handle-empty-table-response';
import {
  compareDates,
  compareText,
  getNextSortDirection,
  includesNormalized,
  sortRows,
} from '../../../../utils/tableUtils';

const SORT_FIELDS = {
  NAME: 'name',
  LAST_NAME: 'last_name',
  IN_PROGRESS_DATE: 'in_progress_date',
  SEND_OUT_DATE: 'send_out_date',
  SHIPPING_NUMBER: 'shipping_number',
};
const SORT_COMPARATORS = {
  [SORT_FIELDS.NAME]: (a, b) => compareText(a.name, b.name),
  [SORT_FIELDS.LAST_NAME]: (a, b) => compareText(a.last_name, b.last_name),
  [SORT_FIELDS.IN_PROGRESS_DATE]: (a, b) => -compareDates(a.in_progress_date, b.in_progress_date),
  [SORT_FIELDS.SEND_OUT_DATE]: (a, b) => compareDates(a.send_out_date, b.send_out_date),
  [SORT_FIELDS.SHIPPING_NUMBER]: (a, b) => compareText(a.shipping_number, b.shipping_number),
};

function TableHacShippingAdmin() {
  const {t} = useTranslation();
  const token = sessionStorage.getItem('token') || null;
  const [dataLoaded, setDataLoaded] = useState(false);

  const [fileDownloaded, setFileDownloaded] = useState(false);
  const { control, getValues } = useForm();


  const [modifiedHacs, setModifiedHacs] = useState(new Set());


  const [hacs, setHacs] = useState([]);
  const [filteredHacs, setFilteredHacs] = useState([]);

  const [filters, setFilters] = useState({
    name: '',
    shipping_number: ''
  });

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('');


  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('in_progress_date');

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleDownloadFile = async () => {
    try {
      const authToken = `Bearer ${sessionStorage.getItem('token')}`;
      const url = `${process.env.REACT_APP_API_URL}/admin/shipping-info-data?download=true`;

      const response = await apiClient.get(url, {
        headers: { Authorization: authToken },
        responseType: 'blob',
      });

      saveAs(response.data, 'addresses.xlsx');
      setFileDownloaded(true);
    } catch (error) {
      console.error('Error downloading file:', error);
    }
  };


  const fetchData = useCallback(async () => {
    if (!token) return;

    const authToken = token;
    const url = `${process.env.REACT_APP_API_URL}/admin/shipping-info-data?download=false`;

    const config = {
      headers: {
        Authorization: `Bearer ${authToken}`,
        'Cache-Control': 'no-cache',
      },
    };

    try {
      const response = await apiClient.get(url, config);
      if (response.status === 200) {
        setHacs(response.data);
        setFilteredHacs(response.data);
        setDataLoaded(true);
      } else if (response.status === 204) {
        handleEmptyTableResponse({
          clearStateSetters: [setHacs, setFilteredHacs],
          setDataLoaded,
        });
      }
    } catch (error) {
      setSnackbarOpen(true);
      setSnackbarSeverity('error');
      setSnackbarMessage(t('common-internal-server-error'));
    }
  }, [token, t]);


  useEffect(() => {

    if (!dataLoaded) {
      fetchData();
    }
  }, [token, dataLoaded, t, fetchData]);


  const applyFilters = () => {
    let filtered = [...hacs];

    const nameSearch = filters.name.trim().toLowerCase();
    const shippingSearch = filters.shipping_number.trim().toLowerCase();

    if (nameSearch) {
      filtered = filtered.filter((item) => {
        const fullName = `${item.name ?? ''} ${item.last_name ?? ''}`.toLowerCase();
        return (
          includesNormalized(item.name, nameSearch) ||
          includesNormalized(item.last_name, nameSearch) ||
          includesNormalized(fullName, nameSearch)
        );
      });
    }

    if (shippingSearch) {
      filtered = filtered.filter(
        (item) =>
          includesNormalized(item.shipping_number, shippingSearch)
      );
    }

    setFilteredHacs(filtered);
  };




  const handleRequestSort = (property) => {
    setOrder(getNextSortDirection(orderBy, order, property));
    setOrderBy(property);
  };

  const sortData = (array) => {
    return sortRows(array, orderBy, order, SORT_COMPARATORS);
  };


  const resetFilters = () => {
    setFilters({ name: '', shipping_number: '' });
    setFilteredHacs(hacs);
  };

  const handleSave = async () => {
    const values = getValues();
    const dataToSend = [];

    hacs.forEach(hac => {
      if (!modifiedHacs.has(hac.id)) return;

      const inProgress = values[`in_progress_date_${hac.id}`];
      const sendOut = values[`send_out_date_${hac.id}`];
      const shippingNumber = values[`shipping_number_${hac.id}`];

      const formattedInProgress = inProgress ? dayjs(inProgress).format('YYYY-MM-DD') : null;
      const formattedSendOut = sendOut ? dayjs(sendOut).format('YYYY-MM-DD') : null;
      const trimmedShippingNumber = shippingNumber?.trim() || null;


      const payload = {
        hac_id: hac.id,
      };

      if (formattedInProgress) payload.in_progress_date = formattedInProgress;
      if (formattedSendOut) payload.send_out_date = formattedSendOut;
      if (trimmedShippingNumber) payload.shipping_number = trimmedShippingNumber;

      dataToSend.push(payload);
    });



    if (dataToSend.length === 0) {
      setSnackbarSeverity('warning');
      setSnackbarMessage(t('common-no-changes'));
      setSnackbarOpen(true);
      return;
    }

    try {
      await apiClient.post('/admin/shipping-info-data', {
        shipping_info_data: dataToSend
      }, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      setSnackbarMessage(t('alert-data-saved'));
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      setDataLoaded(false); // Para que se vuelva a cargar y sincronizar datos
      setModifiedHacs(new Set());

    } catch (error) {
      console.error(error);
      setSnackbarMessage(t('common-internal-server-error'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };



  return (
    <>
      {dataLoaded ? (
        hacs && hacs.length !== 0 ? (
          <div className="shipping-admin-hac">



            <div className="filters">
              <p>{t('common-search')}</p>
              <TableResultsSummary count={filteredHacs.length} />

              <Grid container spacing={3} rowSpacing={0}>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <div className="form-control">
                    <label htmlFor="name">
                      {t('common-name')}/{t('common-last-name')}
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={filters.name}
                      onChange={(e) => setFilters({ ...filters, name: e.target.value })}
                    />
                  </div>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <div className="form-control">
                    <label htmlFor="name">
                      {t('common-shipping-number')}
                    </label>
                    <input
                      type="text"
                      id="shipping_number"
                      name="shipping_number"
                      value={filters.shipping_number}
                      onChange={(e) => setFilters({ ...filters, shipping_number: e.target.value })}
                    />

                  </div>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                  <div className="actions">
                    <button className="btn" onClick={applyFilters}>
                      {t('common-apply')}
                    </button>
                  </div>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <div className="actions">
                    <button className="btn text narrow" onClick={resetFilters}>
                      {t('common-reset-filters')}
                    </button>
                  </div>
                </Grid>
              </Grid>
            </div>

            <div className="actions-shipping-admin">
              <div>
                <button className="btn secondary fit small" onClick={handleSave}>
                  {t('common-save-changes')}
                </button>
              </div>
              <div>
                <button className="btn text small" onClick={handleDownloadFile}>
                  {t('shipping-action')}
                </button>
                {fileDownloaded && (
                  <p className="note">{t('shipping-feedback-ok')}</p>
                )}
              </div>

            </div>

            <div className="table-wrapper">
              {filteredHacs.length !== 0 ? (
                <Table
                  aria-label="simple table"
                  className="user-table shipping-admin-hac"
                >
                  <TableHead>
                    <TableRow>
                      <SortableHeaderCell
                        field={SORT_FIELDS.NAME}
                        orderBy={orderBy}
                        order={order}
                        onRequestSort={handleRequestSort}
                        label={t('common-name')}
                      />
                      <SortableHeaderCell
                        field={SORT_FIELDS.LAST_NAME}
                        orderBy={orderBy}
                        order={order}
                        onRequestSort={handleRequestSort}
                        label={t('common-last-name')}
                      />
                      <TableCell align="left">
                        <span className="text">{t('common-city')}</span>
                      </TableCell>
                      <TableCell align="left">
                        <span className="text">{t('common-country')}</span>
                      </TableCell>
                      <SortableHeaderCell
                        field={SORT_FIELDS.IN_PROGRESS_DATE}
                        orderBy={orderBy}
                        order={order}
                        onRequestSort={handleRequestSort}
                        label={t('common-date-in-progress')}
                      />

                      <SortableHeaderCell
                        field={SORT_FIELDS.SEND_OUT_DATE}
                        orderBy={orderBy}
                        order={order}
                        onRequestSort={handleRequestSort}
                        label={t('common-date-sent')}
                      />

                      <SortableHeaderCell
                        field={SORT_FIELDS.SHIPPING_NUMBER}
                        orderBy={orderBy}
                        order={order}
                        onRequestSort={handleRequestSort}
                        label={t('common-shipping-number')}
                      />


                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortData(filteredHacs).map((row, index) => (
                      <TableRow key={row.id}>
                        <TableCell align="left">
                          <span className="text">{row.name}</span>
                        </TableCell>
                        <TableCell align="left">
                          <span className="text">{row.last_name}</span>
                        </TableCell>
                        <TableCell align="left">
                          <span className="text">{row.city}</span>
                        </TableCell>
                        <TableCell align="left">
                          <span className="text">{row.country}</span>
                        </TableCell>
                        <TableCell align="left">
                          <div className="form-control datepicker">
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                              <Controller
                                name={`in_progress_date_${row.id}`}
                                control={control}
                                defaultValue={row.in_progress_date ? dayjs(row.in_progress_date) : null}
                                render={({ field }) => (
                                  <DateField
                                    {...field}
                                    format="DD/MM/YYYY"
                                    value={field.value}
                                    onChange={(newValue) => {
                                      field.onChange(newValue);
                                      setModifiedHacs(prev => new Set(prev).add(row.id));
                                    }}
                                  />
                                )}
                              />
                            </LocalizationProvider>
                          </div>
                        </TableCell>

                        <TableCell align="left">
                          <div className="form-control datepicker">
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                              <Controller
                                name={`send_out_date_${row.id}`}
                                control={control}
                                defaultValue={row.send_out_date ? dayjs(row.send_out_date) : null}
                                render={({ field }) => (
                                  <DateField
                                    {...field}
                                    format="DD/MM/YYYY"
                                    value={field.value}
                                    onChange={(newValue) => {
                                      field.onChange(newValue);
                                      setModifiedHacs(prev => new Set(prev).add(row.id));
                                    }}
                                  />
                                )}
                              />
                            </LocalizationProvider>
                          </div>
                        </TableCell>

                        <TableCell align="left">
                          <Controller
                            name={`shipping_number_${row.id}`}
                            control={control}
                            defaultValue={row.shipping_number || ''}
                            render={({ field }) => (
                              <input
                                type="text"
                                {...field}
                                onChange={(e) => {
                                  field.onChange(e);
                                  setModifiedHacs(prev => new Set(prev).add(row.id));
                                }}
                              />
                            )}

                          />
                        </TableCell>


                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="no-result">
                  <p className="txt-center">{t('admin-main-tab1-text-empty')}</p>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="no-result">
            <h2 className="txt-center">
              {t('admin-main-tab1-title-empty')}
              <small>{t('admin-main-tab1-text-empty')}</small>
            </h2>
          </div>
        )
      ) : (
        <div>{t('common-loading')}</div>
      )}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
      >
        <Alert severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
}

export default TableHacShippingAdmin;
